import { AreaRow, actionFor } from '../areaTable/AreaTableType'
import { ApiResponse, EwGeneralMsgResponse } from './EWType'

// adjust to however you configure your API base url (CRA: process.env.REACT_APP_API_URL, Vite: import.meta.env.VITE_API_URL)
export const BASE_URL = process.env.REACT_APP_BASE_URL ?? ''
const EW_API = `${BASE_URL}/ew/api/v1`

export interface EwSubmissionInput {
    triggerType: string
    type: string                    // hazard type: HRW, HWN, ...
    warningNumber: string
    warningId: string
    requestName: string
    contactNumber: string
    preRequestDocument: string
    validityStartTime: string       // 'YYYY-MM-DDTHH:mm' (datetime-local) or ISO
    validityEndTime: string
    primaryCause: string
    lightingRiskLevel: string       // spelled like this in the Java DTO
    warningDate: string
    requestersDepartment: string
    messageBodyEnglish: string
    messageCusBodyEnglish: string
    areas: AreaRow[]                // the selected rows
    supportiveDocument?: File | null
}

// Spring's OffsetDateTime binding needs an offset ("Z" or "+05:30").
// datetime-local has none, so read it as browser-local time and send it as UTC ISO.
export const toOffsetIso = (value: string): string => {
    if (!value) return ''
    const d = new Date(value)
    return isNaN(d.getTime()) ? '' : d.toISOString()
}

export const buildEwFormData = (input: EwSubmissionInput): FormData => {
    const fd = new FormData()

    // skip empty values so Spring leaves the field null instead of binding ""
    const put = (key: string, value: string | number | null | undefined) => {
        if (value === undefined || value === null || value === '') return
        fd.append(key, String(value))
    }

    put('triggerType', input.triggerType)
    put('type', input.type)
    put('warningNumber', input.warningNumber)
    put('warningId', input.warningId)
    put('requestName', input.requestName)
    put('contactNumber', input.contactNumber)
    put('preRequestDocument', input.preRequestDocument)
    put('validityStartTime', toOffsetIso(input.validityStartTime))
    put('validityEndTime', toOffsetIso(input.validityEndTime))
    put('primaryCause', input.primaryCause)
    put('lightingRiskLevel', input.lightingRiskLevel)
    put('warningDate', toOffsetIso(input.warningDate))
    put('requestersDepartment', input.requestersDepartment)
    put('messageBodyEnglish', input.messageBodyEnglish)
    put('messageCusBodyEnglish', input.messageCusBodyEnglish)

    // List<RequestAdminDataDto>  ->  effectedAreas[0].code, effectedAreas[0].adminName, ...
    input.areas.forEach((a, i) => {
        const p = `effectedAreas[${i}]`
        put(`${p}.code`, a.code)
        put(`${p}.adminName`, a.name)
        put(`${p}.adminLevel`, a.level)
        put(`${p}.previousAlert`, a.previousAlert)
        put(`${p}.newAlert`, a.newAlert)
        put(`${p}.rainfall`, a.rainfall)
        put(`${p}.lightningRisk`, a.lightning)
        put(`${p}.validityStart`, toOffsetIso(a.startDate))
        put(`${p}.validityEnd`, toOffsetIso(a.endDate))
        put(`${p}.minWind`, a.minWindSpeed)
        put(`${p}.maxWind`, a.maxWindSpeed)
        put(`${p}.minWave`, a.minWaveHeight)
        put(`${p}.maxWave`, a.maxWaveHeight)
        put(`${p}.seaCondition`, a.seaCondition)
        put(`${p}.action`, actionFor(a))   // NEW | CONTINUE | UPGRADE | DOWNGRADE
    })

    if (input.supportiveDocument) {
        fd.append('supportiveDocuments', input.supportiveDocument)
    }

    return fd
}

export const submitEarlyWarning = async (input: EwSubmissionInput, token: string) => {
    const response = await fetch(`${EW_API}/ew_submission`, {
        method: 'POST',
        headers: {
            // do NOT set Content-Type: the browser adds multipart/form-data with the boundary
            Authorization: `Bearer ${token}`,
        },
        body: buildEwFormData(input),
    })

    const body = await response.json().catch(() => null)

    if (!response.ok) {
        throw new Error(body?.message ?? `Request failed (${response.status})`)
    }
    return body
}

// asks the backend for the next warning id for this hazard type (HRW -> "HRW-<next number>").
// Every call uses up a number, so only call it once per page visit.
export const generateWarningKey = async (wType: string, token: string): Promise<string> => {
    const response = await fetch(`${EW_API}/generateProcKey`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ wType }),
    })

    const body = await response.json().catch(() => null)

    if (!response.ok) {
        throw new Error(body?.message ?? `Could not generate warning id (${response.status})`)
    }

    // the id is the "data" part of ResponseDto; adjust if your ResponseDto names it differently
    const key = body?.data
    if (typeof key !== 'string' || key === '') {
        throw new Error('Server did not return a warning id.')
    }
    return key
}

export const generateWarningMessage = async (payload: EwSubmissionInput, token: string): Promise<string> => {
    const res = await fetch(`${EW_API}/generate_warningmsg`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
        },
        body: buildEwFormData(payload),
    })

    if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(text || `Failed to generate message (${res.status})`)
    }

    const json: ApiResponse<EwGeneralMsgResponse> = await res.json()

    return json.data?.messageEnglish ?? ''
}